// FILE: src/pages/CourseInfoPage.jsx
// Course information: re-read the tutorial, Code of Conduct and syllabus at
// any time (MTO 12.4–12.6), plus the glossary and help centre.
import React, { useEffect } from "react";
import { Box, Card, CardActionArea, CardContent, Stack, Typography } from "@mui/material";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import AccessibilityNewOutlinedIcon from "@mui/icons-material/AccessibilityNewOutlined";
import { Link as RouterLink } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchOnboarding } from "../features/onboardingSlice";
import { DetailList, LoadingState, PageHeader, Section } from "../components/ui";
import useCurriculum, { curriculumLine } from "../hooks/useCurriculum";
import SimpleText from "../components/help/SimpleText";

const LINKS = [
  { to: "/student/course-info/tutorial", title: "Tutorial", text: "How the course works: navigation, time rules, identity checks, help and passing.", icon: PlayCircleOutlineIcon },
  { to: "/student/course-info/code-of-conduct", title: "Code of Conduct", text: "Integrity, identity, devices and respectful communication.", icon: GavelOutlinedIcon },
  { to: "/student/course-info/syllabus", title: "Syllabus", text: "What you'll learn, how you're assessed and how the course is organised.", icon: DescriptionOutlinedIcon },
  { to: "/student/glossary", title: "Glossary", text: "What course words and abbreviations mean.", icon: MenuBookOutlinedIcon },
  { to: "/student/help", title: "Help centre", text: "Step-by-step guides, troubleshooting and answers.", icon: HelpOutlineIcon },
  { to: "/student/accessibility", title: "Accessibility", text: "Accessible formats, accommodations and how to give feedback.", icon: AccessibilityNewOutlinedIcon }
];

function formatDay(value) {
  const d = value ? new Date(value) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" }) : null;
}

export default function CourseInfoPage() {
  const curriculum = useCurriculum();
  return (
    <Box>
      <PageHeader title="Course information" description="Everything about how the course works, in one place." />
      {curriculum && (
        <Section
          id="your-curriculum"
          title="Your curriculum"
          description="The approved version of the course you're taking. You finish the course on this version, even if a newer one is approved later."
          sx={{ mb: 2 }}
        >
          <Box data-testid="curriculum-version">
            <DetailList
              items={[
                { label: "Curriculum", value: curriculumLine(curriculum) },
                { label: "MTO approval reference", value: curriculum.mtoApprovalRef || "—" },
                { label: "Approved", value: formatDay(curriculum.approvedAt) || "—" }
              ]}
            />
          </Box>
        </Section>
      )}
      <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" } }}>
        {LINKS.map(({ to, title, text, icon: Icon }) => (
          <Card key={to}>
            <CardActionArea component={RouterLink} to={to} sx={{ height: "100%" }}>
              <CardContent>
                <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", mb: 1 }}>
                  <Icon aria-hidden sx={{ color: "primary.main" }} />
                  <Typography component="h2" sx={{ fontWeight: 650, fontSize: "1.0625rem", textDecoration: "underline", textUnderlineOffset: "0.18em" }}>
                    {title}
                  </Typography>
                </Stack>
                <Typography sx={{ color: "text.secondary" }}>{text}</Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Box>
    </Box>
  );
}

export function TutorialPage() {
  const dispatch = useDispatch();
  const data = useSelector((s) => s.onboarding.data);
  useEffect(() => {
    if (!data) dispatch(fetchOnboarding());
  }, [data, dispatch]);
  if (!data) return <LoadingState variant="page" label="Loading the tutorial…" />;
  const tutorial = data.tutorial || {};
  return (
    <Box>
      <PageHeader
        title="Tutorial"
        description="How the course works."
        breadcrumbs={[{ label: "Course information", to: "/student/course-info" }, { label: "Tutorial" }]}
      />
      <Stack spacing={2} sx={{ maxWidth: 860 }}>
        {tutorial.videoUrl && (
          <Box component="video" controls preload="metadata" src={tutorial.videoUrl} aria-label="Course tutorial video" sx={{ width: "100%", borderRadius: 3, bgcolor: "#000" }}>
            {tutorial.captionsUrl && <track kind="captions" src={tutorial.captionsUrl} srcLang="en" label="English" default />}
          </Box>
        )}
        {(tutorial.sections || []).map((s, i) => (
          <Section key={`${s.heading}-${i}`} id={`tutorial-${i}`} title={s.heading}>
            <SimpleText text={s.body} headingLevel={3} />
          </Section>
        ))}
      </Stack>
    </Box>
  );
}
